// src/lib/firebase.ts
import firebaseConfig from '../../firebaseConfig';
import { Capacitor } from '@capacitor/core';
import { initializeApp, getApp, getApps, type FirebaseApp } from 'firebase/app';
import {
  getAuth,
  initializeAuth,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  inMemoryPersistence,
  browserPopupRedirectResolver,
  type Auth,
  type Persistence,
} from 'firebase/auth';
import { initializeFirestore, getFirestore } from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';
import { getStorage } from 'firebase/storage';

function isNativePlatform(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    try {
      return (globalThis as any)?.Capacitor?.isNativePlatform?.() === true;
    } catch {
      return false;
    }
  }
}

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function isWebKit(): boolean {
  if (!isBrowser()) return false;

  const ua = navigator.userAgent || '';
  return /AppleWebKit/i.test(ua) && !/Chrome|CriOS|Edg|OPR/i.test(ua);
}

const IS_NATIVE = isNativePlatform();
const IS_WEB = isBrowser() && !IS_NATIVE;
const IS_WEBKIT = isWebKit();

const resolvedAuthDomain = (() => {
  if (!IS_WEB) return (firebaseConfig as any).authDomain;

  const host = window.location.hostname;

  if (host === 'roddo.app' || host === 'www.roddo.app') return host;

  return (firebaseConfig as any).authDomain || 'roddo-official.firebaseapp.com';
})();

const appConfig = {
  ...firebaseConfig,
  authDomain: resolvedAuthDomain,
};

export const app: FirebaseApp = getApps().length
  ? getApp()
  : initializeApp(appConfig as any);

function tryInitAuth(persistence: Persistence | Persistence[], isNative: boolean): Auth {
  return initializeAuth(app, {
    persistence,
    ...(isNative ? {} : { popupRedirectResolver: browserPopupRedirectResolver }),
  });
}

function getOrInitAuth(): Auth {
  const anyApp: any = app as any;
  const provider = anyApp?._container?.getProvider?.('auth');
  const already = provider?.isInitialized?.() === true;

  if (already) {
    console.debug('[firebase] auth already initialized; using getAuth');
    return getAuth(app);
  }

  if (IS_NATIVE) {
    try {
      console.debug('[firebase] initializing native JS auth with persistent WebView storage');
      return tryInitAuth([indexedDBLocalPersistence, browserLocalPersistence], true);
    } catch (e) {
      console.warn(
        '[firebase] native persistent auth failed; falling back to browserLocalPersistence',
        e,
      );

      try {
        return tryInitAuth(browserLocalPersistence, true);
      } catch (e2) {
        console.warn(
          '[firebase] native browserLocalPersistence failed; falling back to inMemoryPersistence',
          e2,
        );
        return tryInitAuth(inMemoryPersistence, true);
      }
    }
  }

  try {
    console.debug('[firebase] initializing web JS auth');
    return tryInitAuth([indexedDBLocalPersistence, browserLocalPersistence], false);
  } catch (e) {
    console.warn('[firebase] web auth persistence blocked; falling back to inMemoryPersistence', e);
    return tryInitAuth(inMemoryPersistence, false);
  }
}

export const auth = getOrInitAuth();

try {
  auth.useDeviceLanguage();
} catch {}

const getOrInitFirestore = () => {
  try {
    return initializeFirestore(app, {
      experimentalAutoDetectLongPolling: true,
      ignoreUndefinedProperties: true,
    });
  } catch (e: any) {
    if (e?.code === 'failed-precondition') {
      return getFirestore(app);
    }

    throw e;
  }
};

export const db = getOrInitFirestore();

export const functions = getFunctions(app, 'us-central1');

export const storage = getStorage(app);

try {
  const opts = (app as any)?.options ?? {};

  console.debug('[firebase] init', {
    projectId: opts.projectId,
    appId: opts.appId,
    authDomain: opts.authDomain,
    storageBucket: opts.storageBucket,
    isNative: IS_NATIVE,
    isWebKit: IS_WEBKIT,
    webHost: IS_WEB ? window.location.hostname : null,
  });
} catch {}